import Config from 'src/common/Config';
import { ApiError } from 'src/error/ApiError';
import { Logger } from 'src/utils/Logger';
import * as Sentry from '@sentry/react';
import { redirectToLogin } from 'src/auth/RedirectToLoginPage';
import { apiErrorFingerprint, fetchFailureFingerprint } from './apiErrorFingerprint';

export const API_URL = Config.getApiUrl();

// Track request attempts to avoid logging errors that will be retried by React Query
const requestAttempts = new Map<string, { count: number; lastAttempt: number }>();

// Clean up old entries every 60 seconds
setInterval(() => {
  const now = Date.now();
  for (const [key, value] of requestAttempts.entries()) {
    if (now - value.lastAttempt > 60000) {
      requestAttempts.delete(key);
    }
  }
}, 60000);

export async function fetchWrapper(
  url: string,
  init: RequestInit = {},
  operation = 'undefined',
  {
    timeoutMs = 25000,
    signal: externalSignal,
    fromReactQuery = false,
    expectedStatuses,
  }: {
    timeoutMs?: number;
    signal?: AbortSignal;
    fromReactQuery?: boolean;
    expectedStatuses?: number[];
  } = {},
) {
  const started = performance.now();
  const reqId = Math.random().toString(16).slice(2) + '-' + Date.now().toString(36);
  const hdrs = new Headers(init.headers || {});
  hdrs.set('x-af-cid', reqId);
  const opts: RequestInit = {
    ...init,
    headers: hdrs,
    mode: init.mode ?? 'cors',
    credentials: init.credentials ?? 'include',
  };

  const controller = new AbortController();
  let didTimeout = false;
  const t = setTimeout(() => {
    didTimeout = true;
    controller.abort(`timeout ${timeoutMs}ms`);
  }, timeoutMs);
  opts.signal = externalSignal
    ? AbortSignal.any([controller.signal, externalSignal])
    : controller.signal;

  // Create a key to track this request
  const requestKey = `${operation}:${url}:${init.method ?? 'GET'}`;

  try {
    const res = await fetch(url, opts);
    clearTimeout(t);

    const attemptCount = incrementRequestCount(requestKey);

    const result = await handleResponse(
      res,
      url.toString(),
      operation,
      attemptCount,
      reqId,
      expectedStatuses,
    );

    requestAttempts.delete(requestKey);

    return result;
  } catch (err) {
    if (err instanceof UnauthenticatedError) {
      throw err;
    }

    // If it's an ApiError, it was already logged by handleResponse, so just rethrow
    if (err instanceof ApiError) {
      throw err;
    }

    clearTimeout(t);

    // External abort (user navigation, React Query cancellation) - silent rethrow.
    // AbortSignal.any() can produce non-AbortError exceptions in some browsers,
    // so checking the signal itself is more reliable than inspecting the error.
    if (externalSignal?.aborted) {
      requestAttempts.delete(requestKey);
      throw err;
    }

    // Detect our timeout via our own flag, not the error type.
    // AbortSignal.any() can produce non-AbortError exceptions in some browsers.
    const isOurTimeout = didTimeout;

    if ((err as { name?: string })?.name === 'AbortError' && !isOurTimeout) {
      requestAttempts.delete(requestKey);
      throw err;
    }

    // Body stream already read — the response was aborted while reading JSON.
    // handleResponse already rethrows this silently; keep it silent here too.
    if ((err as { message?: string })?.message?.includes('body stream already read')) {
      requestAttempts.delete(requestKey);
      throw err;
    }

    if (isOurTimeout) {
      const attemptCount = incrementRequestCount(requestKey);
      const shouldLog = !fromReactQuery || attemptCount >= 4;

      if (shouldLog) {
        const duration = performance.now() - started;
        const fullErrorMessage = `Request timed out for ${operation} on ${url.toString()} after ${timeoutMs}ms`;

        Sentry.withScope(scope => {
          scope.setFingerprint(fetchFailureFingerprint('timeout', operation));
          scope.setTag('request_id', reqId);
          scope.setTag('operation', operation);
          scope.setTag('attempt_count', attemptCount.toString());
          scope.setTag('error_type', 'timeout');
          scope.setContext('fetch', {
            url: url.toString(),
            method: opts.method ?? 'GET',
            mode: opts.mode,
            credentials: opts.credentials,
            duration_ms: Math.round(duration),
            timeout_ms: timeoutMs,
            attemptCount: attemptCount,
          });
          Sentry.captureMessage(fullErrorMessage, 'warning');
        });
      }

      throw err;
    }

    const attemptCount = incrementRequestCount(requestKey);

    const duration = performance.now() - started;
    const errorName = (err as { name?: string })?.name ?? 'Unknown';
    const rawErrorMessage = (err as { message?: string })?.message;
    const errorMessage = rawErrorMessage ?? 'Unknown error';

    const fullErrorMessage = `Fetch error for ${operation} on ${url.toString()}: ${errorMessage}`;

    // Only suppress logging for the generic "Failed to fetch" error which is often transient
    // All other errors should be logged immediately as they might be more specific/important
    // Chrome 114+ appends hostname: "Failed to fetch (example.com)"
    // Safari may show: "Load failed", "The network connection was lost", "A server with the specified hostname could not be found"
    // Firefox may show: "NetworkError when attempting to fetch resource"
    // Some browsers throw a TypeError with no message at all — treated as a browser-specific
    // variant of "Failed to fetch"
    const isTransientNetworkError =
      errorName === 'TypeError' &&
      (!rawErrorMessage ||
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('Fetch error') ||
        errorMessage === 'Load failed' ||
        errorMessage.startsWith('Load failed (') ||
        errorMessage === 'NetworkError when attempting to fetch resource' ||
        errorMessage.includes('network connection was lost') ||
        errorMessage.includes('hostname could not be found'));

    const shouldLog = !isTransientNetworkError;
    if (Config.isDev()) {
      // eslint-disable-next-line no-console
      console.error(fullErrorMessage, err, {
        url: url.toString(),
        operation,
        duration_ms: Math.round(duration),
        timeout_ms: timeoutMs,
        attempt_count: attemptCount,
      });
    }

    if (shouldLog) {
      Sentry.withScope(scope => {
        scope.setFingerprint(fetchFailureFingerprint('network', operation));
        scope.setTag('request_id', reqId);
        scope.setTag('operation', operation);
        scope.setTag('attempt_count', attemptCount.toString());
        scope.setContext('fetch', {
          url: url.toString(),
          method: opts.method ?? 'No method in opts',
          mode: opts.mode,
          credentials: opts.credentials,
          duration_ms: Math.round(duration),
          timeout_ms: timeoutMs,
          errorMessage: errorMessage,
          attemptCount: attemptCount,
        });
        Sentry.captureException(new Error(fullErrorMessage));
      });
    }

    throw err;
  }
}

function incrementRequestCount(requestKey: string) {
  const now = Date.now();
  const attemptData = requestAttempts.get(requestKey);
  const attemptCount = attemptData ? attemptData.count + 1 : 1;
  requestAttempts.set(requestKey, { count: attemptCount, lastAttempt: now });
  return attemptCount;
}

export class UnauthenticatedError extends Error {
  constructor() {
    super('401 custom error to trigger login redirect');
  }
}

// A Response body can only be read once, so the status-specific redirects below read a clone.
// Reading the original leaves ApiError.fromApiResponse with a consumed stream, which degrades
// every such response to a detail-less "An error occurred" in Sentry and in the UI.
async function readErrorCode(response: Response): Promise<string | undefined> {
  try {
    const data = await response.clone().json();
    return typeof data?.code === 'string' ? data.code : undefined;
  } catch {
    return undefined;
  }
}

async function handleResponse(
  response: Response,
  resource: string,
  operation: string,
  attemptCount: number,
  reqId: string,
  expectedStatuses: number[] = [],
) {
  // `/public/*` routes never have an authenticated session to expire (they're
  // unauthenticated by convention - see `route_requires_authentication` on the backend), so
  // a 401 from one of them can never legitimately mean "please log in again". It means
  // something else went wrong upstream (e.g. the onboarding-invite submit endpoint returns
  // 401 when the FCA register API's OWN credentials are misconfigured, not the caller's).
  // Hard-redirecting to /login there would silently wipe whatever the caller was doing, so
  // let these fall through to the normal ApiError handling below instead.
  if (response.status === 401 && !isPublicApiRoute(resource)) {
    if (!response.url?.includes('localhost') && !response.url?.includes('afternoon')) {
      // Adding this more for an informative log in case we have a misconfiguration
      // The check is not very robust. However, it should do the job for now.
      // Keeping the same behaviour in this scenario but logging an error, so we get alerted if it happens
      Logger.error(
        `Received 401 from unexpected URL: ${response.url}`,
        new Error('401 from unexpected URL'),
      );
    }
    redirectToLogin();
    throw new UnauthenticatedError();
  }

  if (response.status === 428) {
    if ((await readErrorCode(response)) === 'CREDENTIALS_REQUIRED') {
      window.location.replace('/invite');
      throw new Error('Credentials required - redirecting to complete setup');
    }
  }

  if (response.status === 403) {
    if ((await readErrorCode(response)) === 'SSO_EMAIL_MISMATCH') {
      window.location.replace('/sso-mismatch');
      throw new Error('SSO account mismatch - redirecting to SSO mismatch page');
    }
  }

  if (response.status === 204) {
    return { data: null };
  }

  const uiWarning = response.headers.get('Afternoon-Ui-Warning');
  if (uiWarning) {
    window.dispatchEvent(
      new CustomEvent('afternoon-ui-warning', { detail: decodeURIComponent(uiWarning) }),
    );
  }

  if (response.status < 200 || response.status > 299) {
    const apiError = await ApiError.fromApiResponse(response);

    const errorDetails = apiError.errorId
      ? ` [error_id: ${apiError.errorId}${apiError.errorType ? `, type: ${apiError.errorType}` : ''}]`
      : '';
    const errorMessage = `HTTP ${response.status} error for ${operation} on ${resource}: ${apiError.message}${errorDetails}`;

    // A caller can declare a non-2xx it handles itself (e.g. an optional-resource 404), so it
    // stops being an alert. Server faults are never a caller's expected outcome, so they are
    // always reported regardless of what the caller declared.
    const isServerError = response.status >= 500;
    const isExpectedStatus = !isServerError && expectedStatuses.includes(response.status);

    if (isExpectedStatus) {
      Sentry.addBreadcrumb({
        category: 'api',
        level: 'info',
        message: errorMessage,
        data: { operation, status: response.status, attemptCount },
      });
    } else if (!ApiError.isSuppressedFromSentry(apiError)) {
      Sentry.withScope(scope => {
        scope.setFingerprint(apiErrorFingerprint(operation, response.status));
        scope.setTag('request_id', reqId);
        scope.setTag('operation', operation);
        scope.setTag('status', response.status);
        scope.setTag('attempt_count', attemptCount.toString());
        if (apiError.errorId) {
          scope.setTag('error_id', apiError.errorId);
        }
        if (apiError.errorType) {
          scope.setTag('error_type', apiError.errorType);
        }
        scope.setContext('response', {
          resource,
          status: response.status,
          message: apiError.message,
          attemptCount: attemptCount,
        });

        Sentry.captureException(new Error(errorMessage));
      });
    }

    throw apiError;
  }

  try {
    const data = await response.json();
    return { data };
  } catch (error) {
    const errorName = (error as { name?: string })?.name;
    const errorMessage = (error as { message?: string })?.message ?? '';

    // User navigated away or aborted - don't log, just rethrow
    // AbortError: explicit abort signal
    // TypeError with "body stream already read": abort occurred while reading response
    if (
      errorName === 'AbortError' ||
      (errorName === 'TypeError' && errorMessage.includes('body stream already read'))
    ) {
      throw error;
    }

    Logger.error('Failed to retrieve response JSON', error, {
      resource,
      operation,
      body: await getResponseBodyAsText(response),
    });
    throw new Error('Failed to retrieve response data');
  }
}

function isPublicApiRoute(resource: string): boolean {
  try {
    return new URL(resource).pathname.startsWith('/public/');
  } catch {
    return false;
  }
}

async function getResponseBodyAsText(response: Response) {
  try {
    return await response.text();
  } catch {
    return 'Could not read response body as text';
  }
}
